import {
  Alert,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  UncontrolledDropdown,
} from "reactstrap";
import { SelectPicker } from "rsuite";

import { RecordSummarySection } from "../../Widgets/WidgetSections/RecordSummarySection";
import { StageProgress } from "../../Widgets/WidgetSections/StageProgress";
import RichTextSection from "../../Widgets/WidgetSections/RichTextSection";
import { Field, Module, Section } from "../../../types/TenantInterfaces";
import ShareButtonConfig from "./ShareButtonConfig";
import StrUtils from "../../../utils/StrUtils";

interface Props {
  error: any;
  layoutInfo?: any;
  currentModule: Module;
  sections: Section[];
  setLayout: (val: any) => any;
  handleProgressFieldChange?: (evt: any, section: Section) => void;
  addStageProgressWidget?: () => void;
  addRichTextWidget?: () => void;
  setLayoutInfo?: (val: any) => void;
  publicForm?: boolean;
  fieldOptions?: Field[];
}

const SectionsAndLayouts = ({
  error,
  layoutInfo,
  currentModule,
  sections,
  setLayout,
  handleProgressFieldChange,
  addStageProgressWidget,
  addRichTextWidget,
  setLayoutInfo,
  publicForm = false,
  fieldOptions = [],
}: Props) => {
  const dynamicLayoutFieldAPIName = layoutInfo?.DynamicLayoutAssignment?.Field;
  const dynamicLayoutField = dynamicLayoutFieldAPIName
    ? currentModule?.Fields?.find(
        (f) => f.APIName === dynamicLayoutFieldAPIName
      )
    : undefined;

  return (
    <>
      {error && (
        <div className="d-flex justify-content-end">
          <Alert
            color="danger"
            className="mt-2 py-2 px-4 mb--2"
            // style={{ width: "fit-content" }}
          >
            {error}
          </Alert>
        </div>
      )}
      {!publicForm && (
        <>
          <FormGroup className="mt-3">
            <h5 className="text-muted">Name</h5>
            <Input
              onChange={(e) => handleLayoutInfoChange(e.target.value, "Name")}
              placeholder="Name"
              type="text"
              name="Name"
              value={layoutInfo.Name}
            />
          </FormGroup>
          <FormGroup className="mt-3">
            <h5 className="text-muted">Description</h5>
            <Input
              onChange={(e) =>
                handleLayoutInfoChange(e.target.value, "Description")
              }
              placeholder="Description"
              type="text"
              name="Description"
              value={layoutInfo.Description}
            ></Input>
          </FormGroup>
          <FormGroup className="mt-3">
            <h5 className="text-muted mb-0">Dynamic Layout Assignment</h5>
            <small>
              Automatically apply this layout when a record in the data source
              is assigned a specific value.
            </small>
            <SelectPicker
              block
              value={layoutInfo?.DynamicLayoutAssignment?.Field}
              data={
                currentModule?.Fields?.filter(
                  (f) => f.FieldType === "SingleSelect"
                ) || []
              }
              labelKey="Name"
              valueKey="APIName"
              size="sm"
              className="mt-2"
              placeholder="Select a field to get started"
              onClean={() => {
                if (!setLayoutInfo) return;

                setLayoutInfo((prev: any) => {
                  if (!prev) return prev;

                  return {
                    ...prev,
                    DynamicLayoutAssignment: { Field: "", Value: "" },
                  };
                });
              }}
              onChange={(field) => {
                if (!setLayoutInfo) return;
                setLayoutInfo((prev: any) => {
                  if (!prev || typeof field !== "string") return prev;

                  return {
                    ...prev,
                    DynamicLayoutAssignment: { Field: field, Value: "" },
                  };
                });
              }}
            />
            {layoutInfo?.DynamicLayoutAssignment && (
              <SelectPicker
                block
                // Use sources list of selected field
                data={
                  Array.isArray(dynamicLayoutField?.SourcesList)
                    ? (dynamicLayoutField?.SourcesList as any)
                    : []
                }
                value={layoutInfo?.DynamicLayoutAssignment?.Value}
                onClean={() => {
                  if (!setLayoutInfo) return;
                  setLayoutInfo((prev: any) => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      DynamicLayoutAssignment: {
                        Field: prev.DynamicLayoutAssignment?.Field || "",
                        Value: "",
                      },
                    };
                  });
                }}
                onChange={(value) => {
                  if (!setLayoutInfo) return;
                  setLayoutInfo((prev: any) => {
                    if (!prev || typeof value !== "string") return prev;

                    return {
                      ...prev,
                      DynamicLayoutAssignment: {
                        Field: prev.DynamicLayoutAssignment?.Field || "",
                        Value: value,
                      },
                    };
                  });
                }}
                labelKey="Name"
                valueKey="Value"
                size="sm"
                className="mt-2"
                placeholder="Value"
              />
            )}
          </FormGroup>
          <ShareButtonConfig
            layoutInfo={layoutInfo}
            setLayoutInfo={(value) => {
              if (!setLayoutInfo) return;
              setLayoutInfo(value);
            }}
            currentModule={currentModule}
          />
        </>
      )}
      {/* <FormGroup>
            <h5 className="text-muted">Cover Image</h5>
            {layoutInfo?.CoverImage?.File && (
              <Button
                size="sm"
                color="primary"
                outline
                className="btn-icon-only"
                onClick={() => handleRemoveImage()}
              >
                <i className="mdi mdi-delete-outline"></i>
              </Button>
            )}
            {layoutInfo?.CoverImage?.File ? (
              <img
                src={layoutInfo?.CoverImage?.File}
                alt="Cover Image"
                style={{
                  width: "20%",
                  height: "100px",
                  objectFit: "cover",
                  objectPosition: "100% 100%",
                }}
              />
            ) : (
              <FileDnd upload={uploadCoverImage} uploading={uploading} />
            )}
          </FormGroup> */}
      {!!sections?.length &&
        sections.map((section, index) => {
          section.Type = section.Type ? section.Type : "RecordSummarySection";

          if (section?.Type === "RecordSummarySection") {
            return (
              <RecordSummarySection
                key={section?.id || index}
                section={section}
                i={index}
                reorderSection={reorderSection}
                sectionsLength={sections?.length}
                editMode={true}
                infoChange={handleInfoChange}
                fieldListUpdate={handleFieldListReorder}
                selectedFields={section?.Fields?.map((f: any) =>
                  typeof f?.Field === "string" ? f.Field : f.Field?.APIName
                )}
                removeSection={removeSection}
                removeField={removeField}
                currentModule={currentModule}
                onSelectField={(field: string) => onSelectField(section, field)}
                fieldOptions={fieldOptions}
              />
            );
          }

          if (section?.Type === "StageProgress") {
            return (
              <StageProgress
                key={section?.id || index}
                section={section}
                i={index}
                reorderSection={reorderSection}
                sectionsLength={sections.length}
                editMode={true}
                infoChange={handleInfoChange}
                removeSection={removeSection}
                fieldListUpdate={handleFieldListReorder}
                currentModule={currentModule}
                onSelectStage={onSelectStage}
                removeStage={removeStage}
                selectedStages={section.Stages.map((s: any) => s.Stage)}
                handleProgressFieldChange={handleProgressFieldChange}
              />
            );
          }

          if (section?.Type === "RichTextSection") {
            return (
              <RichTextSection
                key={section?.id || index}
                section={section}
                index={index}
                reorderSection={reorderSection}
                sectionsLength={sections.length}
                editMode={true}
                removeSection={removeSection}
                infoChange={handleInfoChange}
                currentModule={currentModule}
              />
            );
          }

          return <div key={index}>No Section</div>;
        })}
      <Col className="text-lg-right mt-0 pt-3 pb-3">
        <UncontrolledDropdown className="col no-padding">
          <DropdownToggle className="btn-icon add-button">
            <i className="mdi mdi-plus mdi-24px" />
          </DropdownToggle>
          <DropdownMenu className="col no-padding">
            <DropdownItem
              onClick={(e) => {
                e.preventDefault();
                addSection();
              }}
              className="col dropdown-margin-color"
            >
              Add Section
            </DropdownItem>
            {!publicForm && addStageProgressWidget && addRichTextWidget && (
              <>
                <DropdownItem
                  onClick={(e) => {
                    e.preventDefault();
                    addStageProgressWidget();
                  }}
                  className="col dropdown-margin-color"
                >
                  Add Stage Progress Widget
                </DropdownItem>
                <DropdownItem
                  onClick={(e) => {
                    e.preventDefault();
                    addRichTextWidget();
                  }}
                  className="col dropdown-margin-color"
                >
                  Rich Text Widget
                </DropdownItem>
              </>
            )}
          </DropdownMenu>
        </UncontrolledDropdown>
      </Col>
    </>
  );

  function removeField(section: Section, field: { Field: string } | "All") {
    const idx = sections.findIndex((s) => s.id === section.id);
    if (idx < 0) return;

    const selectedSection = sections[idx];

    if (selectedSection.Type !== "RecordSummarySection") return;

    if (typeof field === "string" && field === "All") {
      selectedSection.Fields = [];
    } else {
      const fieldAPIName = field?.Field;

      selectedSection.Fields = selectedSection.Fields.filter((f) => {
        return (
          (f.Field as Field) && (f.Field as Field)?.APIName !== fieldAPIName
        );
      });
    }

    setLayout({ sections });
  }

  function handleLayoutInfoChange(
    value: any,
    fieldName: "Name" | "Description" | "CoverImage"
  ) {
    if (!setLayoutInfo) return; // Layout info not needed in Public Forms

    setLayoutInfo((prev: any) => ({ ...prev, [fieldName]: value }));
  }

  function handleInfoChange(evt: any, sect: Section) {
    const value: string =
      typeof evt?.target?.value === "string" ? evt.target.value : evt;
    const name: "Title" | "Description" | "Field" =
      evt?.target?.name || "Field";
    let idx = sections.findIndex((s) => s.id === sect.id);

    if (idx < 0) return;

    //@ts-ignore
    sections[idx][name] = value;
    setLayout({ sections });
  }

  function handleFieldListReorder(newOrder: string[], section: Section) {
    const matchedSection = sections.find((s) => s.id === section.id);

    if (!matchedSection) return;

    const sectionType = matchedSection.Type;

    if (sectionType === "RecordSummarySection") {
      matchedSection.Fields = newOrder.flatMap((f) => {
        const field = currentModule.Fields.find((f2) => f2.APIName === f);

        if (!field) return [];

        return [{ Field: field, ColSpan: 1 }];
      });

      setLayout({ sections });
    }
    if (sectionType === "StageProgress") {
      matchedSection.Stages = newOrder.map((s) => {
        return { Stage: s, ColSpan: 1 };
      });

      setLayout({ sections });
    }
  }

  function removeSection(id: string) {
    const index = sections.findIndex((section) => section?.id === id);

    if (index === -1) return;

    sections.splice(index, 1);

    setLayout({ sections });
  }

  function onSelectField(section: Section, field: string) {
    if (section.Type !== "RecordSummarySection") return;

    let finalField;
    const startingLength = section.Fields.length;

    section.Fields = section.Fields.filter((f: any) =>
      typeof f?.Field === "string" ? f?.Field : f.Field?.APIName !== field
    );

    if (startingLength !== section.Fields.length) {
      return setLayout({ sections });
    }

    for (let i = 0; i < currentModule.Fields.length; i++) {
      if (currentModule.Fields[i].APIName === field) {
        finalField = structuredClone(currentModule.Fields[i]);
      }
    }

    const idx = sections.findIndex((s) => s.id === section.id);

    if (idx < 0) return;

    // @ts-ignore
    sections[idx].Fields.push({
      Field: finalField,
      ColSpan: 1,
    });

    setLayout({ sections });
  }

  function removeStage(section: Section, stage: { Stage: string } | "All") {
    const idx = sections.findIndex((s) => s.id === section.id);

    if (idx < 0) return;

    const selectedSection = sections[idx];

    if (selectedSection.Type !== "StageProgress") return;

    if (typeof stage === "string" && stage === "All") {
      selectedSection.Stages = [];
    } else {
      selectedSection.Stages = selectedSection.Stages.filter((f) => {
        return f.Stage !== stage.Stage;
      });
    }

    setLayout({ sections });
  }

  function reorderSection(sect: Section, i: number) {
    let idx = sections.findIndex((s) => s.id === sect.id);

    if (idx < 0) {
      return;
    }

    if (idx + i < 0 || idx + i > sections.length - 1) {
      return;
    }

    let temp = sections[idx + i];

    sections[idx + i] = sect;
    sections[idx] = temp;

    setLayout({ sections });
  }

  // Function to initiate a RecordSummarySection component
  function addSection() {
    let id = StrUtils.uuidv4();

    sections.push({
      id,
      Type: "RecordSummarySection",
      Title: "New Section",
      Description: "",
      Fields: [],
    });

    setLayout({
      sections,
      [`addField${id}`]: "",
    });
  }

  function onSelectStage(section: Section, stage: any) {
    if (section.Type !== "StageProgress") return;

    let progressField = section.ProgressField;

    const startingLength = section.Stages.length;

    section.Stages = section.Stages.filter((s: any) => s?.Stage !== stage);

    if (startingLength !== section.Stages.length) {
      return setLayout({ sections });
    }

    const field = currentModule.Fields?.find(
      (f) => f.APIName === progressField
    );

    if (!field) return;

    const idx = sections.findIndex((s) => s.id === section.id);
    if (idx < 0) return;

    const selectedSection = sections[idx];

    if (selectedSection.Type !== "StageProgress") return;

    selectedSection.Stages.push({
      Stage: stage,
      ColSpan: 1,
    });

    setLayout({
      sections,
    });
  }
};

export default SectionsAndLayouts;
